import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Alert, Avatar, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, Grid, InputAdornment, MenuItem,
  Stack, TextField, Typography,
} from '@mui/material';
import {
  Search as SearchIcon, Person as PersonIcon, History as HistoryIcon,
  Business as OfficeIcon, LockOutlined as ReadOnlyIcon, Description as ReportIcon,
} from '@mui/icons-material';
import { reportKindLabel } from './ReportUpload';
import { openReportFile } from '../pages/Reports';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const SOAP = [
  ['subjective', 'Subjective'], ['objective', 'Objective'],
  ['assessment', 'Assessment'], ['plan', 'Plan'],
];

/** A shared patient's report, opened through the sharing endpoint (audit-logged there). */
function SharedReport({ patientId, r }) {
  const by = r.uploaded_by_role === 'patient' ? 'the patient' : (r.uploaded_by_name || 'their office');
  return (
    <Chip
      icon={<ReportIcon fontSize="small" />}
      label={`${r.title} · ${reportKindLabel(r)}`}
      title={`Added by ${by}`}
      size="small" variant="outlined" clickable
      onClick={() => openReportFile(`${API_URL}/api/v1/sharing/patients/${patientId}/reports/${r.id}/file`)}
    />
  );
}

/** Visits newest first, each with its reports; reports on no visit are their own entries. */
function sharedTimeline(visits = [], reports = []) {
  const ids = new Set(visits.map((v) => v.visit_id));
  const onVisit = {};
  reports.forEach((r) => { if (r.session_id && ids.has(r.session_id)) (onVisit[r.session_id] = onVisit[r.session_id] || []).push(r); });
  return [
    ...visits.map((v) => ({ kind: 'visit', key: `v-${v.visit_id}`, day: v.date || '', v, reports: onVisit[v.visit_id] || [] })),
    ...reports.filter((r) => !(r.session_id && ids.has(r.session_id))).map((r) => ({
      kind: 'report', key: `r-${r.id}`, day: r.report_date || r.visit_date || (r.created_at || '').slice(0, 10), r,
    })),
  ].sort((a, b) => (b.day || '').localeCompare(a.day || ''));
}

/**
 * Patients other offices have chosen to share with this session — read-only.
 *
 * An office shares "across" (with its organisation's other offices) or "up" (with its
 * region, organisation and enterprise). The server decides which offices that is for
 * whoever is looking; this only renders the answer. Every open is audit-logged.
 *
 * `canSeeHistory` is false for staff, who don't see visit history at their own office
 * either.
 */
export default function SharedPatients({ canSeeHistory = true, emptyHint }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [office, setOffice] = useState('all');
  const [open, setOpen] = useState(null);           // { patient, visits } | { loading, patient }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API_URL}/api/v1/sharing/patients`);
      setData(d);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load shared patients');
      setData({ patients: [], offices: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.patients || []).filter((p) => (office === 'all' || p.clinic_id === office)
      && (!q || `${p.full_name} ${p.reason || ''} ${p.clinic_name || ''}`.toLowerCase().includes(q)));
  }, [data, search, office]);

  const openHistory = async (p) => {
    setOpen({ loading: true, patient: p });
    try {
      const { data: d } = await axios.get(`${API_URL}/api/v1/sharing/patients/${p.patient_id}/history`);
      setOpen({ patient: { ...p, ...d.patient }, visits: d.visits || [], reports: d.reports || [] });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load their history');
      setOpen(null);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  const offices = data?.offices || [];
  if (!offices.length) {
    return (
      <Alert severity="info">
        {emptyHint || 'No office is sharing its patients with you yet. An office’s administrator can turn sharing on in Settings → Patient sharing.'}
      </Alert>
    );
  }

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField fullWidth size="small" placeholder="Search by name, reason or office…"
              value={search} onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: (
                <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>
              ) }} />
            <TextField select size="small" label="Office" value={office}
              onChange={(e) => setOffice(e.target.value)} sx={{ minWidth: 240 }}>
              <MenuItem value="all">All sharing offices ({offices.length})</MenuItem>
              {offices.map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
            </TextField>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
            <ReadOnlyIcon sx={{ fontSize: 14 }} />
            Read-only. Visits are recorded by the patient’s own office. Every record you open is logged.
          </Typography>
        </CardContent>
      </Card>

      {!rows.length ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          {search || office !== 'all' ? 'No shared patient matches that.' : 'The sharing offices have no patients yet.'}
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {rows.map((p) => (
            <Grid item xs={12} md={6} lg={4} key={p.patient_id}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                    <Avatar sx={{ bgcolor: 'secondary.main', width: 48, height: 48 }}><PersonIcon /></Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h6" fontWeight={600} noWrap>{p.full_name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        DOB: {p.date_of_birth || '—'}
                      </Typography>
                    </Box>
                  </Box>
                  <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                    <Chip size="small" icon={<OfficeIcon />} label={p.clinic_name} />
                    {p.reason && <Chip size="small" variant="outlined" color="primary" label={p.reason} />}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    {p.visit_count
                      ? `${p.visit_count} visit${p.visit_count === 1 ? '' : 's'} · last ${p.last_visit}`
                      : 'No recorded visits yet'}
                    {p.provider_name ? ` · ${p.provider_name}` : ''}
                  </Typography>
                  {canSeeHistory && (
                    <Button fullWidth variant="outlined" startIcon={<HistoryIcon />} onClick={() => openHistory(p)}>
                      View history
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={Boolean(open)} onClose={() => setOpen(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          {open?.patient?.full_name}
          <Typography variant="body2" color="text.secondary">
            Shared by {open?.patient?.clinic_name} · read-only
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {open?.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : !open?.visits?.length && !open?.reports?.length ? (
            <Typography color="text.secondary">No recorded visits or reports yet.</Typography>
          ) : sharedTimeline(open.visits, open.reports).map((item, i) => (
            <Box key={item.key} sx={{ mb: 2.5 }}>
              {i > 0 && <Divider sx={{ mb: 2.5 }} />}
              {item.kind === 'report' ? (
                <>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
                    <Typography fontWeight={700}>{item.day}</Typography>
                    <Chip size="small" label="Report" />
                  </Stack>
                  <SharedReport patientId={open.patient.patient_id} r={item.r} />
                  {item.r.notes && <Typography variant="body2" sx={{ mt: 0.75 }}>{item.r.notes}</Typography>}
                </>
              ) : (
                <>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
                    <Typography fontWeight={700}>{item.v.date}</Typography>
                    <Chip size="small" label={item.v.status} />
                    <Typography variant="body2" color="text.secondary">{item.v.doctor_name}</Typography>
                  </Stack>
                  {item.v.summary && <Typography variant="body2" sx={{ mb: 1 }}>{item.v.summary}</Typography>}
                  {SOAP.filter(([k]) => item.v.soap?.[k]).map(([k, label]) => (
                    <Box key={k} sx={{ mb: 0.75 }}>
                      <Typography variant="caption" fontWeight={700} color="text.secondary">{label}</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{item.v.soap[k]}</Typography>
                    </Box>
                  ))}
                  {!!item.v.icd10_codes?.length && (
                    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {item.v.icd10_codes.map((c, j) => (
                        <Chip key={j} size="small" variant="outlined"
                          label={typeof c === 'string' ? c : `${c.code || ''} ${c.description || ''}`.trim()} />
                      ))}
                    </Stack>
                  )}
                  {item.reports.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" fontWeight={700} color="text.secondary">Reports from this visit</Typography>
                      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {item.reports.map((r) => <SharedReport key={r.id} patientId={open.patient.patient_id} r={r} />)}
                      </Stack>
                    </Box>
                  )}
                </>
              )}
            </Box>
          ))}
        </DialogContent>
        <DialogActions><Button onClick={() => setOpen(null)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
